import * as React from "react";
import { Grid, Typography } from "@mui/material";
import {
  addressLookup,
  formatDate,
  toTitleCase,
} from "../../common/services/Utilities";
import AddressDisplay from "../AddressDisplay";
import {
  SUR_ADDRESS,
  ACQ_ADDRESS,
  OFFICE_ADDRESS,
  PHYSICAL_ADDRESS,
  NEW_INSTITUTIONS,
  BUSINESS_COMBINATIONS,
  BUSINESS_COMBINATIONS_FAILURES,
  TITLE_CHANGES,
  MAIN_OFFICE_RELOCATIONS,
  LIQUIDATIONS,
  TRUST_POWERS,
  PANDAS,
  OFFICE_OPENINGS,
  OFFICE_CLOSINGS,
  BRANCH_OFFICE_RELOCATIONS,
  INTERIM_MERGERS,
  CONVERSIONS,
} from "../../refdata/constants";
import { KeyValue, OscrLevelTwoRecord } from "../../interfaces";
import LinkComponent from "../LinkComponent";

export default function OSCRTransactionDetails({
  data,
  eventType,
}: {
  data: OscrLevelTwoRecord;
  eventType: string;
}) {
  function getEventTypeValues() {
    const eventLookup: KeyValue = {
      [NEW_INSTITUTIONS]: {
        CERT: data.CERT,
        CLASS: data.CLASS,
        REGAGENT: data.REGAGENT,
        CHARTAGENT: data.CHARTAGENT,
      },
      [BUSINESS_COMBINATIONS]: {
        CERT: data.SUR_CERT,
        EFF_BRANCHES: data.ACQ_BRANCHES,
        INST_ADDRESS: (
          <AddressDisplay
            addressObject={addressLookup(data)[SUR_ADDRESS]}
            wrapAddress={true}
          />
        ),
      },
      [BUSINESS_COMBINATIONS_FAILURES]: {
        CERT: data.SUR_CERT,
        EFF_BRANCHES: data.ACQ_BRANCHES,
        CHANGECODE: data.SUR_CHANGECODE,
        CHANGECODE_DESC: data.SUR_CHANGECODE_DESC,
        INST_ADDRESS: (
          <AddressDisplay
            addressObject={addressLookup(data)[SUR_ADDRESS]}
            wrapAddress={true}
          />
        ),
      },
      [INTERIM_MERGERS]: {
        CERT: data.CERT,
      },
      [CONVERSIONS]: {
        CERT: data.CERT,
        CNTYNAME: data.CNTYNAME,
        REGION: data.FDICREGION_DESC,
      },
      [TITLE_CHANGES]: {
        CERT: data.CERT,
        CNTYNAME: data.CNTYNAME,
        REGION: data.FDICREGION_DESC,
      },
      [MAIN_OFFICE_RELOCATIONS]: {
        CERT: data.CERT,
        CNTYNAME: data.CNTYNAME,
        REGION: data.FDICREGION_DESC,
      },
      [LIQUIDATIONS]: {
        CERT: data.CERT,
        CLASS: data.CLASS,
        CNTYNAME: data.CNTYNAME,
        REGION: data.FDICREGION_DESC,
        LIQUIDATED: data.ACQ_BRANCHES,
      },
      [TRUST_POWERS]: {
        CERT: data.CERT,
        CNTYNAME: data.CNTYNAME,
        REGION: data.FDICREGION_DESC,
      },
      [PANDAS]: {
        CERT: data.ACQ_CERT,
        CNTYNAME: data.ACQ_CNTYNAME,
        REGION: data.ACQ_FDICREGION_DESC,
        TRUST: data.ACQ_TRUST,
        CLASS: data.CLASS,
        ACQ_BRANCHES: data.ACQ_BRANCHES,
        INST_ADDRESS: (
          <AddressDisplay
            addressObject={addressLookup(data)[ACQ_ADDRESS]}
            wrapAddress={true}
          />
        ),
      },
      [BRANCH_OFFICE_RELOCATIONS]: {
        CERT: data.CERT,
        OFF_NAME: data.OFF_NAME,
        OFF_NUM: data.OFF_NUM,
        CLASS: data.CLASS,
        REGION: data.FDICREGION_DESC,
        UNINUM: data.UNINUM,
      },
      [OFFICE_OPENINGS]: {
        CERT: data.CERT,
        OFF_NAME: data.OFF_NAME,
        OFF_NUM: data.OFF_NUM,
        CLASS: data.CLASS,
        CNTYNAME: data.OFF_CNTYNAME,
        REGION: data.FDICREGION_DESC,
        OFF_SERVTYPE: data.OFF_SERVTYPE_DESC,
        UNINUM: data.UNINUM,
        BRANCH_ADDRESS: (
          <AddressDisplay
            addressObject={addressLookup(data)[OFFICE_ADDRESS]}
            wrapAddress={true}
          />
        ),
      },
      [OFFICE_CLOSINGS]: {
        CERT: data.CERT,
        OFF_NAME: data.OFF_NAME,
        OFF_NUM: data.OFF_NUM,
        CLASS: data.CLASS,
        CNTYNAME: data.OFF_CNTYNAME,
        REGION: data.FDICREGION_DESC,
        OFF_SERVTYPE: data.OFF_SERVTYPE_DESC,
        UNINUM: data.UNINUM,
        BRANCH_ADDRESS: (
          <AddressDisplay
            addressObject={addressLookup(data)[OFFICE_ADDRESS]}
            wrapAddress={true}
          />
        ),
      },
    };

    const values = eventLookup[eventType];
    Object.keys(values).forEach((key: string) => {
      if (values[key] === "") {
        values[key] = " ";
      }
    });
    return values;
  }

  const EventDetail = (
    key: string,
    label: string,
    value: string,
    condensed?: boolean,
  ) => (
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiGrid-root": {
          "paddingBottom": 1,
          "& p": {
            lineHeight: 1.3,
            color: "#666666",
          },
          "& strong": {
            color: (theme) => theme.palette.primary.main,
          },
        },
      }}
      key={`${key}-container`}
    >
      {condensed ? (
        <Grid key={`${key}-grid-item`} size={12}>
          <Typography id={`${key}-title`} align="center" key={`${key}-title`}>
            <strong>{label}</strong>
          </Typography>
          <Typography id={`${key}-value`} key={`${key}-value`} component="div">
            {value}
          </Typography>
        </Grid>
      ) : (
        <>
          <Grid key={`${key}-grid-item-label`} size={6}>
            <Typography id={`${key}-title`} key={`${key}-title`} align="right">
              <strong>{label}</strong>
            </Typography>
          </Grid>
          <Grid key={`${key}-grid-item-value`} size={6}>
            <Typography id={`${key}-value`} key={`${key}-value`} align="left">
              {value}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );

  const centerColumn: KeyValue = {
    "Transaction ID": data.TRANSNUM,
    "Action Taken": getEventTypeValues().CHANGECODE
      ? `${getEventTypeValues().CHANGECODE} - ${toTitleCase(
          getEventTypeValues().CHANGECODE_DESC,
        )}`
      : `${data.CHANGECODE} - ${toTitleCase(data.CHANGECODE_DESC)}${
          data.CHANGECODE === 224 && data.CHANGECODE_DESC.slice(-1) !== ")"
            ? ")"
            : ""
        }`,
    "Process Date": formatDate(data.PROCDATE),
    "Effective Date": formatDate(data.EFFDATE),
    "Affected Branches": getEventTypeValues().EFF_BRANCHES
      ? getEventTypeValues().EFF_BRANCHES.toLocaleString()
      : undefined,
    "Office Name": toTitleCase(getEventTypeValues().OFF_NAME),
    "Office Number": getEventTypeValues().OFF_NUM,
    "Unique Number": getEventTypeValues().UNINUM,
    "Service Type": toTitleCase(getEventTypeValues().OFF_SERVTYPE),
    "Class": getEventTypeValues().CLASS,
    "County": toTitleCase(getEventTypeValues().CNTYNAME),
    "FDIC Region": toTitleCase(getEventTypeValues().REGION),
    "Trust": getEventTypeValues().TRUST,
    "Liquidated Branches": getEventTypeValues().LIQUIDATED
      ? getEventTypeValues().LIQUIDATED.toLocaleString()
      : undefined,
    "Acquired Branches": getEventTypeValues().ACQ_BRANCHES
      ? getEventTypeValues().ACQ_BRANCHES.toLocaleString()
      : undefined,
    "Primary Regulator": getEventTypeValues().REGAGENT,
    "Charter Authority": getEventTypeValues().CHARTAGENT,
  };

  const rightColumn: KeyValue = {
    "Main Office Address": getEventTypeValues().INST_ADDRESS ? (
      getEventTypeValues().INST_ADDRESS
    ) : (
      <AddressDisplay
        addressObject={addressLookup(data)[PHYSICAL_ADDRESS]}
        wrapAddress={true}
      />
    ),
    "Primary Website": getEventTypeValues().WEBSITE,
    "Branch Address": getEventTypeValues().BRANCH_ADDRESS,
    "View Branches": getEventTypeValues().LEVEL_4,
  };

  return (
    <Grid
      container
      spacing={1}
      sx={{
        "& .MuiGrid-root": {
          "margin": "0 auto",
          "& p": {
            color: "#666666",
          },
          "& strong": {
            color: (theme) => theme.palette.primary.main,
          },
        },
        "paddingBottom": 2,
      }}
    >
      <Grid size={12}>
        <Grid container>
          <Grid
            size={{
              sm: 4,
              xs: 12,
            }}
          >
            <Typography align="center">
              <Typography
                sx={{
                  fontWeight: "bold",
                  marginRight: 1,
                }}
                component="span"
                color="primary.main"
              >
                FDIC Cert#
              </Typography>
              <Typography component="span" id={`fdic-cert-value`}>
                {getEventTypeValues().CERT}
              </Typography>
            </Typography>

            <Typography
              component="div"
              sx={{
                marginBottom: "16px",
              }}
            >
              <LinkComponent
                appName={"NameLocation"}
                cert={getEventTypeValues().CERT}
              />
            </Typography>
          </Grid>
          <Grid
            size={{
              sm: 4,
              xs: 12,
            }}
          >
            {Object.keys(centerColumn).map((key: string) => {
              return centerColumn[key]
                ? EventDetail(
                    key.replace(/\s+/g, "-").toLowerCase(),
                    key,
                    centerColumn[key],
                  )
                : "";
            })}
          </Grid>
          <Grid
            size={{
              sm: 4,
              xs: 12,
            }}
          >
            {Object.keys(rightColumn).map((key: string) => {
              return rightColumn[key]
                ? EventDetail(
                    key.replace(/\s+/g, "-").toLowerCase(),
                    key,
                    rightColumn[key],
                    true,
                  )
                : "";
            })}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
